import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import Button from "../UI/Button/Button";
import UniversalTable from "../UI/Table/UniversalTable.tsx";
import OrderEditor from "../components/OrderEditor";
import ActionsMenu from "../UI/ActionsMenu/ActionsMenu.tsx";
import FilterDialog from "../components/FilterDialog.tsx";

import {
  getOrders,
  upsertOrder,
  deleteOrder,
  getOrderDetails,
  getNomenclatures,
  deleteOrderItem,
} from "../api/enterprise";

import { notifyError } from "../utils/toasts";

function OrdersPage({ enterpriseId, divisionId }) {
  const { t } = useTranslation();

  /* ------------------------- state ------------------------- */
  const [orders, setOrders]   = useState([]);
  const [meta, setMeta]       = useState(null);

  const [searchTerm, setSearchTerm]     = useState("");
  const [sortColumn, setSortColumn]     = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingOrder, setLoadingOrder]   = useState(false);

  const [nomenclatures, setNomenclatures] = useState([]);

  /* фильтр */
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters]         = useState({});

  /* ----------------------- fetch --------------------------- */
  const fetchOrders = async () => {
    try {
      const res = await getOrders(divisionId /*, filters */);
      setOrders(res.data);
      setMeta(res.meta || null);
    } catch (e) {
      notifyError(e?.response?.data?.message || e.message);
    }
  };

  useEffect(() => {
    if (divisionId) fetchOrders();
  }, [divisionId, filters]);

  useEffect(() => {
    if (!enterpriseId) return;
    (async () => {
      try {
        const noms = await getNomenclatures(enterpriseId);
        setNomenclatures(noms);
      } catch (e) {
        notifyError(e.message);
      }
    })();
  }, [enterpriseId]);

  /* ---------------- local search / sort ------------------- */
  const searched = useMemo(
    () =>
      orders.filter((o) =>
        (o.name || "").toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [orders, searchTerm]
  );

  const sorted = useMemo(() => {
    if (!sortColumn || !sortDirection) return searched;
    return [...searched].sort((a, b) => {
      const A = a[sortColumn] ?? "";
      const B = b[sortColumn] ?? "";
      return sortDirection === "asc" ? (A > B ? 1 : -1) : A > B ? -1 : 1;
    });
  }, [searched, sortColumn, sortDirection]);

  const totalCount  = sorted.length;
  const currentPageData = sorted.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /* ------------------- table columns ---------------------- */
  const columns = [
    { key: "id", title: t("orders.table.id"), isSortable: true, renderCell: r => r.id },
    { key: "name", title: t("orders.table.name"), isSortable: true, renderCell: r => r.name },
    { key: "priority", title: t("orders.table.priority"), isSortable: true, renderCell: r => r.priority },
    { key: "status", title: t("orders.table.status"), isSortable: true, renderCell: r => r.status },
    {
      key: "manufactureDates",
      title: t("orders.table.production_dates"),
      renderCell: r => `${r.planning_start || ""} — ${r.planning_end || ""}`,
    },
    { key: "created_at", title: t("orders.table.created"), isSortable: true, renderCell: r => r.created_at },
    {
      key: "actions",
      title: "",
      width: "60px",
      renderCell: (row) => (
        <ActionsMenu
          onDelete={() => confirmDelete(row.id)}
          onPrint={() => console.log("print", row.id)}
          onExport={() => console.log("export", row.id)}
        />
      ),
    },
  ];

  /* ------------------- Filter fields ---------------------- */
  const nomOptions = nomenclatures.map((n) => ({ value: n.id, label: n.name }));
  const emptyOpts  = []; // пока нет API пользователей

  const filterFields = [
    { type: "select", key: "nomenclature", label: t("Состав"),    options: nomOptions },
    { type: "select", key: "priority",     label: t("Приоритет"), options: [
        { value:"low",    label: t("Низкий")   },
        { value:"medium", label: t("Средний") },
        { value:"high",   label: t("Высокий") },
    ]},
    { type: "select", key: "status", label: t("Статус"), options: [
        { value:"draft",    label: t("Черновик")   },
        { value:"approved", label: t("Одобрен")    },
        { value:"in_work",  label: t("На проверку") },
        { value:"rework",   label: t("На доработку") },
    ]},

    { type: "dateRange", label: t("Дата начала производства"),
      keyFrom:"prodStartFrom", keyTo:"prodStartTo" },

    { type: "dateRange", label: t("Дата окончания производства"),
      keyFrom:"prodEndFrom", keyTo:"prodEndTo" },

    {
      type: "group", label: t("Создан"), children: [
        { type: "select",   key:"created_user", label: t("Кто создал заказ"), options: emptyOpts },
        { type: "dateRange", keyFrom:"createdFrom", keyTo:"createdTo", label:"" },
      ],
    },
    {
      type: "group", label: t("Изменён"), children: [
        { type: "select",   key:"modified_user", label: t("Кто внёс изменения"), options: emptyOpts },
        { type: "dateRange", keyFrom:"modifiedFrom", keyTo:"modifiedTo", label:"" },
      ],
    },
  ];

  /* -------------------- delete confirm -------------------- */
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orderToDelete, setOrderToDelete]       = useState(null);

  const confirmDelete = (id, e) => {
    if (e) e.stopPropagation();
    setOrderToDelete(id);
    setShowConfirmModal(true);
  };
  const handleConfirmDelete = async () => {
    if (orderToDelete) {
      try {
        await deleteOrder(orderToDelete);
        fetchOrders();
      } catch (e) {
        notifyError(e.message);
      }
    }
    setShowConfirmModal(false);
    setOrderToDelete(null);
  };

  /* -------------------- filter handlers ------------------- */
  const handleApplyFilters = (applied) => {
    setFilters(applied);
    setShowFilters(false);
  };
  const handleResetFilters = () => {
    setFilters({});
    setShowFilters(false);
  };

  /* ------------------------- JSX -------------------------- */
  return (
    <Box sx={{ mt: 0, px: 0 }}>
      <UniversalTable
        columns={columns}
        data={currentPageData}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onRowClick={(row) => {
          setLoadingOrder(true);
          getOrderDetails(row.id)
            .then(setSelectedOrder)
            .catch((e) => notifyError(e.message))
            .finally(() => setLoadingOrder(false));
        }}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={setSortColumn}
        totalCount={meta?.total ?? totalCount}
        page={currentPage}
        pageSize={itemsPerPage}
        onChangePage={setCurrentPage}
        onChangePageSize={setItemsPerPage}
        onFilterClick={() => setShowFilters(true)}
      />

      <FilterDialog
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        fields={filterFields}
        filters={filters}
        setFilters={setFilters}
      />

      {/* Confirm delete dialog */}
      <Dialog open={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <DialogTitle>{t("confirm_delete_title")}</DialogTitle>
        <DialogContent>{t("confirm_delete_message")}</DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {loadingOrder && <div>{t("loading_order_data")}</div>}

      {selectedOrder && !loadingOrder && (
        <OrderEditor
          order={selectedOrder}
          nomenclatures={nomenclatures}
          divisionId={divisionId}
          onSave={async (name, items) => {
            await upsertOrder(divisionId, selectedOrder.id, name, items);
            fetchOrders();
          }}
          onDeleteItem={deleteOrderItem}
        />
      )}

      {!selectedOrder && !loadingOrder && (
        <OrderEditor
          nomenclatures={nomenclatures}
          divisionId={divisionId}
          onSave={async (name, items) => {
            await upsertOrder(divisionId, null, name, items);
            fetchOrders();
          }}
        />
      )}
    </Box>
  );
}

export default OrdersPage;

/* import React, { useState, useEffect } from "react";
import OrdersTable from "../components/OrdersTable";
import OrderEditor from "../components/OrderEditor";
import {
  getOrders,
  upsertOrder,
  deleteOrder,
  getOrderDetails,
  getNomenclatures,
  getTechnologicalCardsForNomenclature,
  deleteOrderItem,
} from "../api/enterprise";
import { useTranslation } from "react-i18next";
import { notifyError } from "../utils/toasts";

function OrdersPage({ enterpriseId, divisionId }) {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [nomenclatures, setNomenclatures] = useState([]);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const fetchOrders = async () => {
    try {
      const result = await getOrders(divisionId);
      setOrders(result);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [divisionId]);

  useEffect(() => {
    const fetchNomenclatures = async () => {
      try {
        const result = await getNomenclatures(enterpriseId);
        setNomenclatures(result);
      } catch (error) {
        notifyError(error?.response?.data?.message || error.message);
      }
    };
    fetchNomenclatures();
  }, [enterpriseId]);

  const handleEditOrder = async (order) => {
    setLoadingOrder(true);
    setSelectedOrder(null);

    try {
      const detailedOrder = await getOrderDetails(order.order_id);
      setSelectedOrder(detailedOrder);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    } finally {
      setLoadingOrder(false);
    }
  };

  const handleAddOrder = async (name, orderItems) => {
    try {
      await upsertOrder(divisionId, null, name, orderItems);
      await fetchOrders();
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };

  const handleEditOrderSave = async (orderId, newName, orderItems) => {
    try {
      await upsertOrder(divisionId, orderId, newName, orderItems);
      const updatedOrder = await getOrderDetails(orderId);
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.order_id === orderId ? updatedOrder : order
        )
      );
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await deleteOrder(orderId);
      await fetchOrders();
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };

  const handleDeleteOrderItem = async (orderItemId) => {
    try {
      await deleteOrderItem(orderItemId);
      console.log("Позиция заказа успешно удалена", orderItemId);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };

  return (
    <div className="container mt-4">
      <h1>{t("orders")}</h1>
      <OrdersTable
        orders={orders}
        onEdit={handleEditOrder}
        onDelete={handleDeleteOrder}
        onAdd={() => setSelectedOrder(null)}
      />
      {loadingOrder && <div>Загрузка данных заказа...</div>}
      {selectedOrder && !loadingOrder && (
        <OrderEditor
          order={selectedOrder}
          nomenclatures={nomenclatures}
          
          divisionId={divisionId}
          onSave={(newName, orderItems) =>
            handleEditOrderSave(selectedOrder.order_id, newName, orderItems)
          }
          onDeleteItem={(itemId) => handleDeleteOrderItem(itemId)}
        />
      )}
      {!selectedOrder && !loadingOrder && (
        <OrderEditor
          nomenclatures={nomenclatures}
           onSave={handleAddOrder}
          divisionId={divisionId}
        />
      )}
    </div>
  );
}

export default OrdersPage;
 */